import { useMemo } from 'react';
import { toDateKey } from '@features/quote/utils/dateKey';
import type { ConfiguredService } from '../types/service.types';
import type { ConfiguredAddOn } from '../types/addon.types';
import type { SummaryCalculations, ScheduleItem } from '../types/calculations.types';
import type { PricedBasket } from '@features/quote/types/basketPricing.types';
import { decimalToNumber } from '@features/quote/utils/decimalMoney';
import { isIndependentLink } from '../utils/linkedAddOns';

export const useServicePlanSummary = (
    configuredServices: ConfiguredService[],
    configuredAddOns: ConfiguredAddOn[],
    pricing: PricedBasket | undefined
) => {
    const summary = useMemo((): SummaryCalculations | null => {
        if (!pricing) {
            return null;
        }
        const { subtotals, totals } = pricing;

        return {
            servicesTotal: decimalToNumber(subtotals.services.net),
            addOnsTotal: decimalToNumber(subtotals.addOns.net),
            subTotal: decimalToNumber(totals.net),
            vat: decimalToNumber(totals.vat),
            total: decimalToNumber(totals.gross),
        };
    }, [pricing]);

    const schedule = useMemo((): ScheduleItem[] => {
        const scheduleItems: ScheduleItem[] = [];

        // Group add-ons by service ID for easy lookup
        const addOnsByService = configuredAddOns.reduce<Record<string, ConfiguredAddOn[]>>(
            (acc, addOn) => {
                if (!isIndependentLink(addOn.linkedServiceId)) {
                    if (!acc[addOn.linkedServiceId]) {
                        acc[addOn.linkedServiceId] = [];
                    }
                    acc[addOn.linkedServiceId].push(addOn);
                }
                return acc;
            },
            {}
        );

        // Add services with their linked add-ons
        configuredServices.forEach((service) => {
            const serviceKey = toDateKey(service.startDate);
            if (serviceKey) {
                scheduleItems.push({
                    item: service,
                    type: 'Service',
                    linkedAddOns: addOnsByService[service.instanceId] || [],
                    startDate: serviceKey,
                    cost: service.price,
                });
            }
        });

        // Add independent add-ons (not linked to any service)
        configuredAddOns.forEach((addOn) => {
            const addOnKey = toDateKey(addOn.startDate);
            if (isIndependentLink(addOn.linkedServiceId) && addOnKey) {
                scheduleItems.push({
                    item: addOn,
                    type: 'Add-On',
                    linkedAddOns: [],
                    startDate: addOnKey,
                    cost: addOn.price,
                });
            }
        });

        // Sort by start date
        scheduleItems.sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));

        return scheduleItems;
    }, [configuredServices, configuredAddOns]);

    return {
        summary,
        schedule,
    };
};
